import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, Phone, MapPin, ArrowRight, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo, projectsData } from '../data/mock';

const Footer = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar' || language === 'ku';
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  return (
    <footer className="bg-black border-t border-white/10 pt-20 pb-10" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16 mb-16">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <img
              src="https://customer-assets.emergentagent.com/job_f5e23208-e2e1-478e-b660-88f2702726f3/artifacts/s61bg8wu_erasebg-transformed.png"
              alt="Darwazay Garmyan"
              className="h-10 w-auto brightness-0 invert mb-8"
            />
            <p className="text-white/50 text-sm font-light leading-relaxed mb-8 max-w-md">
              {t('footer.about')}
            </p>
            <div className={`flex ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
              <a
                href={companyInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 border border-white/20 flex items-center justify-center text-white/50 hover:bg-white hover:text-black hover:border-white transition-all duration-300"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={companyInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 border border-white/20 flex items-center justify-center text-white/50 hover:bg-white hover:text-black hover:border-white transition-all duration-300"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-white mb-8 font-light">
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-4">
              {[
                { path: '/', label: t('nav.home') },
                { path: '/about', label: t('nav.about') },
                { path: '/services', label: t('nav.services') },
                { path: '/careers', label: t('nav.careers') },
                { path: '/contact', label: t('nav.contact') }
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className={`text-sm text-white/50 hover:text-white transition-colors duration-300 font-light inline-flex items-center gap-2 group ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    <ArrowIcon className={`w-3 h-3 opacity-0 ${isRTL ? 'translate-x-2 group-hover:-translate-x-0' : '-translate-x-2 group-hover:translate-x-0'} group-hover:opacity-100 transition-all duration-300`} />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Projects - Always show English names */}
          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-white mb-8 font-light">
              {t('footer.projects')}
            </h3>
            <ul className="space-y-4">
              {projectsData.map((project) => (
                <li key={project.id}>
                  <Link
                    to={`/projects/${project.id}`}
                    className={`text-sm text-white/50 hover:text-white transition-colors duration-300 font-light inline-flex items-center gap-2 group ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    <ArrowIcon className={`w-3 h-3 opacity-0 ${isRTL ? 'translate-x-2 group-hover:-translate-x-0' : '-translate-x-2 group-hover:translate-x-0'} group-hover:opacity-100 transition-all duration-300`} />
                    {project.name.en}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Information */}
        <div className="border-t border-white/10 pt-12 mb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className={`flex items-start ${isRTL ? 'space-x-reverse space-x-5' : 'space-x-5'}`}>
              <div className="w-10 h-10 border border-white/20 flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 text-white/50" />
              </div>
              <div>
                <h4 className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3 font-light">
                  {language === 'en' ? 'Email' : language === 'ar' ? 'البريد الإلكتروني' : 'ئیمەیڵ'}
                </h4>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="text-sm text-white/70 hover:text-white transition-colors duration-300 font-light"
                  dir="ltr"
                >
                  {companyInfo.email}
                </a>
              </div>
            </div>

            <div className={`flex items-start ${isRTL ? 'space-x-reverse space-x-5' : 'space-x-5'}`}>
              <div className="w-10 h-10 border border-white/20 flex items-center justify-center flex-shrink-0">
                <Phone className="w-4 h-4 text-white/50" />
              </div>
              <div>
                <h4 className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3 font-light">
                  {language === 'en' ? 'Phone' : language === 'ar' ? 'الهاتف' : 'تەلەفۆن'}
                </h4>
                <div className="space-y-1">
                  {companyInfo.phone.slice(0, 2).map((phone) => (
                    <a
                      key={phone}
                      href={`tel:${phone.replace(/\s/g, '')}`}
                      className="block text-sm text-white/70 hover:text-white transition-colors duration-300 font-light"
                      dir="ltr"
                    >
                      {phone}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className={`flex items-start ${isRTL ? 'space-x-reverse space-x-5' : 'space-x-5'}`}>
              <div className="w-10 h-10 border border-white/20 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-white/50" />
              </div>
              <div>
                <h4 className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3 font-light">
                  {language === 'en' ? 'Address' : language === 'ar' ? 'العنوان' : 'ناونیشان'}
                </h4>
                <p className="text-sm text-white/70 font-light leading-relaxed">
                  {companyInfo.address[language] || companyInfo.address.en}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-[10px] uppercase tracking-[0.15em] text-white/30 font-light">
            {t('footer.rights')}
          </p>
          <p className="text-[10px] uppercase tracking-[0.15em] text-white/30 font-light">
            {language === 'en' ? 'Designed with Excellence' : language === 'ar' ? 'مصمم بامتياز' : 'دیزاین کراوە بە شیاوی'}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
